/**
 * Centralized Axios instance with authentication
 *
 * This module provides a configured axios instance that automatically
 * includes Clerk authentication tokens in all requests.
 */

import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { createLogger } from './logger';

const log = createLogger('axiosInstance');

// Get base URL from environment or use default
const getBaseURL = (): string => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 
    (process.env.NODE_ENV === 'production' 
      ? 'https://ai-travel-920175119171.europe-west3.run.app'
      : 'http://localhost:8080');
  return apiBaseUrl;
};

// Create axios instance with default configuration
const axiosInstance: AxiosInstance = axios.create({
  baseURL: getBaseURL(),
  withCredentials: true,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  },
});

// Store the auth token getter function (will be set by ClerkAuthProvider)
let authTokenGetter: (() => Promise<string | null>) | null = null;

/**
 * Set the function to get the current auth token from Clerk
 * This should be called once during app initialization
 */
export const setAuthTokenGetter = (getter: () => Promise<string | null>) => {
  authTokenGetter = getter;
  log.debug('Auth token getter configured');
};

/**
 * Returns the current Clerk auth token if available.
 */
export const getCurrentAuthToken = async (): Promise<string | null> => {
  if (!authTokenGetter) {
    return null;
  }

  try {
    return await authTokenGetter();
  } catch (error) {
    log.error('Error getting auth token', error);
    return null;
  }
};

/**
 * Builds an Authorization header map if a token is available.
 */
export const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const token = await getCurrentAuthToken();
  if (token) {
    return {
      Authorization: `Bearer ${token}`,
    };
  }
  return {};
};

// Request interceptor to add auth token
axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Get auth token if available
    if (config.headers) {
      const token = await getCurrentAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    log.debug('HTTP request', {
      url: config.url,
      method: config.method,
      hasAuth: !!config.headers?.Authorization,
    });

    return config;
  },
  (error) => {
    log.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for logging and error handling
axiosInstance.interceptors.response.use(
  (response) => {
    log.debug('HTTP response', {
      url: response.config.url,
      status: response.status,
    });
    return response;
  },
  (error) => {
    // Enhanced error logging for CORS and network issues
    const isNetworkError = !error.response && error.request;
    const isCorsError = error.message?.includes('CORS') || 
                       error.message?.includes('Network Error') ||
                       (isNetworkError && error.code === 'ERR_NETWORK');

    log.error('Response interceptor error', {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
      isNetworkError,
      isCorsError,
      url: error.config?.url,
      baseURL: error.config?.baseURL,
    });

    // Handle CORS/Network errors with helpful message
    if (isCorsError || isNetworkError) {
      const baseUrl = getBaseURL();
      log.error('CORS or network error detected', {
        baseUrl,
        url: error.config?.url,
        fullUrl: error.config?.baseURL ? `${error.config.baseURL}${error.config.url}` : error.config?.url,
      });
      
      // Enhance error message for better debugging
      const enhancedError = new Error(
        `Network/CORS error: Unable to connect to ${baseUrl}${error.config?.url || ''}. ` +
        `Please check that the backend is running and CORS is configured correctly. ` +
        `If deployed, ensure FRONTEND_URL environment variable is set on the backend.`
      );
      enhancedError.name = error.name || 'NetworkError';
      return Promise.reject(enhancedError);
    }

    // Handle 401 Unauthorized - user needs to sign in
    if (error.response?.status === 401) {
      log.warn('Unauthorized request - user may need to sign in');
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
